import React, { useEffect, useState } from "react";
import { FiRefreshCw } from "react-icons/fi";
import { relativeTime } from "../utils/oversightFormat";

/** How often an open panel re-renders so "3m ago" doesn't sit frozen. */
const TICK_MS = 60 * 1000;

/**
 * "Synced 3m ago" plus a refresh control, for a panel that paints from cache
 * and revalidates behind the scenes — so it's visible when you're looking at
 * something stale.
 *
 * Deliberately does NOT reuse `RefreshButton`: that one is a 40px circular
 * control sized for a page header, which overwhelms a dock card's title bar.
 * The formatting comes from the shared `relativeTime`.
 *
 * The 60s tick is mounted here, so it exists only while a panel using this is
 * on screen — there is no app-wide timer.
 *
 * @param {Object} props
 * @param {number|string|null} props.syncedAt - Epoch ms or ISO of the last
 *   authoritative fetch. Null renders "not synced yet" rather than a dash,
 *   which is the real state for a background room nobody has opened.
 * @param {() => void} props.onRefresh
 * @param {boolean} [props.refreshing=false]
 * @param {string} [props.label='Synced']
 */
export function SyncStatus({ syncedAt, onRefresh, refreshing = false, label = "Synced" }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // relativeTime takes an ISO string or anything Date can parse; epoch ms
  // needs converting or it reads as 1970.
  const iso =
    syncedAt == null
      ? null
      : typeof syncedAt === "number"
      ? new Date(syncedAt).toISOString()
      : syncedAt;

  return (
    <span className="flex flex-shrink-0 items-center gap-1">
      <span
        className="whitespace-nowrap text-[10px] text-gray-500"
        title={iso ? `Last synced ${new Date(iso).toLocaleString()}` : undefined}
      >
        {iso ? `${label} ${relativeTime(iso)}` : "Not synced yet"}
      </span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        title="Refresh"
        aria-label="Refresh"
        className="rounded p-1 text-gray-500 transition-colors hover:bg-white/60 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FiRefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
      </button>
    </span>
  );
}

export default SyncStatus;
